import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { MessageBar, MessageBarType } from "@fluentui/react";

interface Toast {
  message: string;
  prUrl?: string;
  type?: "success" | "error" | "warning" | "info";
  duration?: number; // in milliseconds, undefined means infinite (manual dismiss only)
  dismissible?: boolean; // whether user can dismiss manually
}

interface ToastContextType {
  showToast: (toast: Toast) => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export const useToast = () => useContext(ToastContext);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [toast, setToast] = useState<Toast | null>(null);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<number | null>(null);

  // Keep timeoutRef in sync with timeoutId
  useEffect(() => {
    timeoutRef.current = timeoutId;
  }, [timeoutId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const showToast = useCallback(
    (toast: Toast) => {
      // Clear any existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }

      setToast(toast);

      // Set up auto-dismiss only if duration is explicitly specified and greater than 0
      if (toast.duration !== undefined && toast.duration > 0) {
        const newTimeoutId = window.setTimeout(() => {
          setToast(null);
          setTimeoutId(null);
        }, toast.duration);
        setTimeoutId(newTimeoutId);
      }
    },
    [timeoutId],
  );

  const dismissToast = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setToast(null);
  }, [timeoutId]);

  // Helper function to convert toast type to MessageBarType
  const getMessageBarType = (type?: string) => {
    switch (type) {
      case "error":
        return MessageBarType.error;
      case "warning":
        return MessageBarType.warning;
      case "info":
        return MessageBarType.info;
      case "success":
      default:
        return MessageBarType.success;
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {toast && (
        <MessageBar
          messageBarType={getMessageBarType(toast.type)}
          isMultiline={false}
          onDismiss={toast.dismissible !== false ? dismissToast : undefined}
          dismissButtonAriaLabel="Close"
        >
          {toast.message}
        </MessageBar>
      )}
      {children}
    </ToastContext.Provider>
  );
};
